import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, Gift, Trophy, Star, Target, Calendar, ShoppingBag } from 'lucide-react';
import { useRewards } from '../context/RewardsContext';

const RewardsPage = () => {
  const navigate = useNavigate();
  const {
    points,
    totalPointsEarned,
    ownedPrizes,
    unlockedAchievements,
    stats,
    weeklyGoal,
    claimWeeklyGoal,
    buyPrize,
    getDailyChallenges,
    VIRTUAL_PRIZES,
    ACHIEVEMENTS,
  } = useRewards();

  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(null);

  const dailyChallenges = getDailyChallenges();
  const completedDaily = dailyChallenges.filter(c => c.completed).length;

  const handleBuyPrize = (prizeId) => {
    const success = buyPrize(prizeId);
    if (success) {
      setShowPurchaseSuccess(prizeId);
      setTimeout(() => setShowPurchaseSuccess(null), 2000);
    }
  };

  const handleClaimWeekly = () => {
    claimWeeklyGoal();
  };

  const categories = ['all', 'stickers', 'pets', 'badges', 'special'];

  const filteredPrizes = selectedCategory === 'all' 
    ? VIRTUAL_PRIZES 
    : VIRTUAL_PRIZES.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-lg font-semibold hover:scale-110 transition-transform bg-white/50 hover:bg-white/70"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-4">
            <Card className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white" style={{ borderRadius: '16px' }}>
              <div className="flex items-center gap-2">
                <span className="text-3xl">⭐</span>
                <div>
                  <p className="text-xs opacity-90">Your Points</p>
                  <p className="text-2xl font-bold">{points}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: '📊' },
            { id: 'daily', label: 'Daily Challenges', icon: '🎯' },
            { id: 'shop', label: 'Prize Shop', icon: '🛒' },
            { id: 'collection', label: 'My Collection', icon: '🎁' },
            { id: 'achievements', label: 'Achievements', icon: '🏆' },
          ].map(tab => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-semibold whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-white text-purple-600' 
                  : 'bg-white/30 text-white hover:bg-white/50'
              }`}
              style={{ borderRadius: '12px' }}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Points Summary */}
            <Card className="p-6 bg-white/95" style={{ borderRadius: '20px' }}>
              <div className="text-center">
                <div className="text-6xl mb-4">⭐</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Total Points Earned</h3>
                <p className="text-4xl font-bold text-purple-600">{totalPointsEarned}</p>
              </div>
            </Card>

            {/* Stats Card */}
            <Card className="p-6 bg-white/95" style={{ borderRadius: '20px' }}>
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Trophy className="w-6 h-6 text-yellow-500" />
                Your Stats
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Quizzes Completed</span>
                  <span className="font-bold text-purple-600">{stats.quizzesCompleted}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Correct Answers</span>
                  <span className="font-bold text-green-600">{stats.correctAnswers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Games Played</span>
                  <span className="font-bold text-blue-600">{stats.gamesPlayed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Streak</span>
                  <span className="font-bold text-orange-600">{stats.currentStreak} 🔥</span>
                </div>
              </div>
            </Card>

            {/* Weekly Goal */}
            <Card className="p-6 bg-white/95" style={{ borderRadius: '20px' }}>
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Calendar className="w-6 h-6 text-blue-500" />
                Weekly Goal
              </h3>
              <div className="text-center mb-4">
                <p className="text-gray-600 mb-2">Complete {weeklyGoal.lessonsTarget} lessons this week</p>
                <p className="text-3xl font-bold text-purple-600">
                  {weeklyGoal.lessonsCompleted} / {weeklyGoal.lessonsTarget}
                </p>
              </div>
              <Progress 
                value={(weeklyGoal.lessonsCompleted / weeklyGoal.lessonsTarget) * 100} 
                className="h-4 mb-4" 
              />
              {weeklyGoal.lessonsCompleted >= weeklyGoal.lessonsTarget ? (
                weeklyGoal.claimed ? (
                  <div className="text-center text-green-600 font-bold">
                    ✅ Claimed! +50 points
                  </div>
                ) : (
                  <Button
                    onClick={handleClaimWeekly}
                    className="w-full py-3 bg-gradient-to-r from-green-400 to-emerald-500 text-white font-bold"
                    style={{ borderRadius: '12px' }}
                  >
                    🎉 Claim +50 Points!
                  </Button>
                )
              ) : (
                <p className="text-center text-gray-500">
                  {weeklyGoal.lessonsTarget - weeklyGoal.lessonsCompleted} more to go!
                </p>
              )}
            </Card>

            {/* How to Earn Points */}
            <Card className="p-6 bg-white/95 md:col-span-2 lg:col-span-3" style={{ borderRadius: '20px' }}>
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Star className="w-6 h-6 text-yellow-500" />
                How to Earn Points
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl">
                  <div className="text-3xl mb-2">📚</div>
                  <p className="font-bold text-blue-700">+10 points</p>
                  <p className="text-sm text-blue-600">Complete a lesson</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-green-100 to-green-200 rounded-xl">
                  <div className="text-3xl mb-2">✅</div>
                  <p className="font-bold text-green-700">+2 points</p>
                  <p className="text-sm text-green-600">Correct answer</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl">
                  <div className="text-3xl mb-2">🎯</div>
                  <p className="font-bold text-orange-700">+15 points</p>
                  <p className="text-sm text-orange-600">Daily challenge</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl">
                  <div className="text-3xl mb-2">🗓️</div>
                  <p className="font-bold text-purple-700">+50 points</p>
                  <p className="text-sm text-purple-600">Weekly goal</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Daily Challenges Tab */}
        {activeTab === 'daily' && (
          <div className="space-y-4">
            <Card className="p-6 bg-white/95" style={{ borderRadius: '20px' }}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Target className="w-7 h-7 text-orange-500" />
                  Today's Challenges
                </h3>
                <div className="text-lg font-bold text-purple-600">
                  {completedDaily} / {dailyChallenges.length} Done
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dailyChallenges.map(challenge => (
                  <div
                    key={challenge.id}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      challenge.completed 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-4xl">{challenge.emoji}</div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800">{challenge.name}</h4>
                        <p className="text-sm text-gray-600">{challenge.description}</p>
                        <div className="mt-2">
                          <Progress 
                            value={(challenge.progress / challenge.target) * 100} 
                            className="h-2" 
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {challenge.progress} / {challenge.target}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {challenge.completed ? (
                          <span className="text-2xl">✅</span>
                        ) : (
                          <span className="font-bold text-orange-500">+{challenge.reward}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Prize Shop Tab */}
        {activeTab === 'shop' && (
          <div className="space-y-6">
            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {categories.map(cat => (
                <Button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 capitalize ${
                    selectedCategory === cat 
                      ? 'bg-white text-purple-600' 
                      : 'bg-white/30 text-white'
                  }`}
                  style={{ borderRadius: '12px' }}
                >
                  {cat === 'all' ? '🎁 All' : 
                   cat === 'stickers' ? '⭐ Stickers' :
                   cat === 'pets' ? '🐱 Pets' :
                   cat === 'badges' ? '🏆 Badges' : '💎 Special'}
                </Button>
              ))}
            </div>

            {/* Prize Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredPrizes.map(prize => {
                const owned = ownedPrizes.includes(prize.id);
                const canAfford = points >= prize.cost;
                const isPurchasing = showPurchaseSuccess === prize.id;
                
                return (
                  <Card 
                    key={prize.id}
                    className={`relative p-4 text-center transition-transform duration-200 ${
                      owned ? 'bg-green-50 border-2 border-green-300' : 'bg-white/95'
                    } ${!isPurchasing ? 'hover:scale-105' : ''}`}
                    style={{ borderRadius: '16px' }}
                  >
                    {isPurchasing && (
                      <div className="absolute inset-0 flex items-center justify-center bg-green-500/90 z-10" style={{ borderRadius: '16px' }}>
                        <div className="text-white text-center">
                          <div className="text-4xl mb-2">🎉</div>
                          <p className="font-bold">Got it!</p>
                        </div>
                      </div>
                    )}
                    <div className="text-5xl mb-3">{prize.emoji}</div>
                    <h4 className="font-bold text-gray-800 mb-1">{prize.name}</h4>
                    <p className="text-xs text-gray-500 mb-3">{prize.description}</p>
                    
                    {owned ? (
                      <div className="text-green-600 font-bold">✅ Owned</div>
                    ) : (
                      <Button
                        onClick={() => handleBuyPrize(prize.id)}
                        disabled={!canAfford || isPurchasing}
                        className={`w-full py-2 ${
                          canAfford 
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                            : 'bg-gray-300 text-gray-500'
                        }`}
                        style={{ borderRadius: '10px' }}
                      >
                        ⭐ {prize.cost}
                      </Button>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* My Collection Tab */}
        {activeTab === 'collection' && (
          <Card className="p-6 bg-white/95" style={{ borderRadius: '20px' }}>
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <Gift className="w-7 h-7 text-pink-500" />
              My Collection ({ownedPrizes.length} items)
            </h3>
            
            {ownedPrizes.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🎁</div>
                <p className="text-xl text-gray-600">No prizes yet!</p>
                <p className="text-gray-500">Visit the Prize Shop to get some cool rewards!</p>
                <Button
                  onClick={() => setActiveTab('shop')}
                  className="mt-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                  style={{ borderRadius: '12px' }}
                >
                  Go to Shop 🛒
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {ownedPrizes.map(prizeId => {
                  const prize = VIRTUAL_PRIZES.find(p => p.id === prizeId);
                  if (!prize) return null;
                  return (
                    <div 
                      key={prizeId}
                      className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl"
                    >
                      <div className="text-5xl mb-2">{prize.emoji}</div>
                      <p className="text-sm font-bold text-gray-700">{prize.name}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        )}

        {/* Achievements Tab */}
        {activeTab === 'achievements' && (
          <Card className="p-6 bg-white/95" style={{ borderRadius: '20px' }}>
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <Trophy className="w-7 h-7 text-yellow-500" />
              Achievements ({unlockedAchievements.length} / {ACHIEVEMENTS.length})
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ACHIEVEMENTS.map(achievement => {
                const unlocked = unlockedAchievements.includes(achievement.id);
                return (
                  <div
                    key={achievement.id}
                    className={`p-4 rounded-xl border-2 flex items-center gap-4 ${
                      unlocked 
                        ? 'bg-yellow-50 border-yellow-300' 
                        : 'bg-gray-50 border-gray-200 opacity-60'
                    }`}
                  >
                    <div className={`text-4xl ${!unlocked && 'grayscale'}`}>
                      {achievement.emoji}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-800">{achievement.name}</h4>
                      <p className="text-sm text-gray-600">{achievement.description}</p>
                    </div>
                    <div className="text-right">
                      {unlocked ? (
                        <span className="text-2xl">✅</span>
                      ) : (
                        <span className="text-sm text-gray-400">+{achievement.reward} pts</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RewardsPage;
